// --------------------------
// IMPORTS
// --------------------------
import { useEffect, useState } from "react";
import { Form, Input, InputNumber } from "antd";
import { useDispatch, useSelector } from "react-redux";
import TableContent from "../../components/atoms/TableContent";
import TopContainerHeader from "../../components/molecules/TopComponentHeader";
import useCurrentPath, {
  deFaultHandlePaginationChange,
  formateDateTime,
  handleDelete,
} from "../../utils/Constants";
import {
  getLocationListThunk,
  deleteLocationThunk,
  addLocationThunk,
  editLocationThunk,
} from "../../store/thunks/locationsThunk";

import media from "../../media";
import EditForm from "../../components/molecules/Forms/editForm";
import { selectedRowInfo } from "../../store/slices/authSlice";

// --------------------------
// REUSABLE CELL COMPONENT
// --------------------------
const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === "number" ? <InputNumber /> : <Input />;
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

// --------------------------
// MAIN COMPONENT
// --------------------------
const Locations = () => {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const currentPath = useCurrentPath();
  const [form] = Form.useForm();
  const [tableForm] = Form.useForm();

  // --------------------------
  // REDUX STATE
  // --------------------------
  const storeRole = useSelector((state) => state.auth.role);
  const storeLIUId = useSelector((state) => state.auth.lIUId);
  const locations = useSelector((state) => state.locations.list) || [];
  const locationsLoading = useSelector(
    (state) => state.locations.loading
  );
  const storePageInfo = useSelector((state) => state.locations.pageInfo);

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [storeTotalCount, setStoreTotalCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(true);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [filteredLocations, setFilteredLocations] = useState([]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);

  const [values, setValues] = useState({
    name: "",
    history: "",
    createdAt: "",
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [editingKey, setEditingKey] = useState("");
  const isEditing = (record) => record.key === editingKey;

  // --------------------------
  // API CALLS
  // --------------------------

  // Fetch locations
  const fetchLocations = async () => {
    setLoadingCount(true);
    const res = await dispatch(
      getLocationListThunk({
        route: currentPath,
        pagination: {
          current: 1,
          pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
        },
      })
    ).unwrap();
    setStoreTotalCount(res.totalCount || 0);
    setShowLoader(false);
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------
  useEffect(() => {
    fetchLocations();
  }, [currentPath, dispatch]);

  useEffect(() => {
    if (Array.isArray(locations) && locations.length > 0) {
      setFilteredLocations(locations);
    }
  }, [locations]);

  // --------------------------
  // HANDLERS
  // --------------------------
  const openEditModal = (record) => {
    setEditingLocation(record); // store the record to edit
    tableForm.setFieldsValue({
      location: record.location,
      locationCode: record.locationCode,
      locationCurrencyCode: record.locationCurrencyCode,      
    });
    setIsEditModalOpen(true); // open the modal
  };

  // --------------------------
  // TABLE COLUMNS
  // --------------------------
  const columns = [
    {
      title: "Sr. No.",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Location Name",
      dataIndex: "location",
      editable: true,
      render: (text) => <>{text}</>,      
    },
    {
      title: "Location Code",
      dataIndex: "locationCode",
      editable: true,
      render: (text) => <>{text}</>,      
    },
    {
      title: "Location Currency Code",
      dataIndex: "locationCurrencyCode",
      editable: true,
      render: (text) => <>{text}</>,      
    },
    {
      title: "Status",
      dataIndex: "status",
      editable: true,
      className: "all-nowrap-column",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (text) => (
        <>
          <span className={`${text?.toLowerCase()}Status`}>{text}</span>
        </>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (text) => text && formateDateTime(text),
    },
    ...(storeRole === process.env.REACT_APP_SROLE ||
    storeRole === process.env.REACT_APP_ADROLE
      ? [
          {
            title: "Updated At",
            dataIndex: "updatedAt",
            render: (text) => text && formateDateTime(text),
          },
          {
            title: "Action",
            dataIndex: "action",
            render: (_, record) => (
              <span
                className="table-round-btn"
                onClick={() => openEditModal(record)}
              >
                <img src={media.editIcon.svg} alt="Edit icon" />
                <span className="text-size-12 dark-black-color">Edit</span>
              </span>
            ),
          },
        ]
      : []),
  ];

  const mergedColumns =
    columns &&
    columns.map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record) => ({
          record,
          inputType: "text",
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
        }),
      };
    });

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      const selectedRowKeysChildInfoArr = selectedRows.map((row) => row.key);

      dispatch(
        selectedRowInfo({
          selectedRowKeysChildInfo: selectedRowKeysChildInfoArr,
        })
      );
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  // --------------------------
  // FORM FIELDS
  // --------------------------
  const formFields = [
    {
      isVisible: true,
      label: "Location Name",
      name: "location",
      rules: [
        {
          required: true,
          message: "Please enter location name",
        },
      ],
      placeholder: "Enter the location name",
      type: "input",
      onChange: (e) => {
        setValues({
          ...values,
          location: e.target.value,
        });
      },
    },
    {
      isVisible: true,
      label: "Location Code",
      name: "locationCode",
      rules: [
        {
          required: true,
          message: "Please enter location code",
        },
      ],
      placeholder: "Enter the location code",
      type: "input",
      onChange: (e) => {
        setValues({
          ...values,
          locationCode: e.target.value,
        });
      },
    },
    {
      isVisible: true,
      label: "Location Currency Code",
      name: "locationCurrencyCode",
      rules: [
        {
          required: true,
          message: "Please enter location currency code",
        },
      ],
      placeholder: "Enter the location currency code",
      type: "input",
      onChange: (e) => {
        setValues({
          ...values,
          locationCurrencyCode: e.target.value,
        });
      },
    }
  ];

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <div className="container">
      <div className="bgi-no-repeat card-stretch rounded-lg p-0">
        <TopContainerHeader
          sectionNameClass="col-lg-7 text-left commonTitleText"
          sectionTitle="Locations"
          sectionDescription={
            showLoader ? "Loading..." : `${storeTotalCount} Found`
          }
          hasSectionButton={false}
          hasSearch={true}
          hasSearchButton="general"
          addNewButtonClassName="btn mx-auto px-5 py-2 defaultbutton rounded-lg"
          name="Add New Location"
          searchClassName="table-search"
          searchPlaceholder="Search Location's by location's name, location's code, location's currency code"
          onButtonClick={() => setIsAddModalOpen(true)}
          searchData={locations}
          onFilteredData={setFilteredLocations}
          searchKeys={["location","locationCode","locationCurrencyCode"]}
          addFormModalOpen={isAddModalOpen}
          addFormSetIsModalOpen={setIsAddModalOpen}
          addFormModalShowLoader={setShowLoader}
          payload={(values) => ({
            location: values?.location,
            locationCode: values?.locationCode,
            locationCurrencyCode: values?.locationCurrencyCode,
            createdAt: new Date().toISOString(),
            historySource: "backend",
            historyAction: "create",
            historyUserID: storeLIUId,
            route: currentPath,
          })}
          list={addLocationThunk}
          formFields={formFields}
          successMessage="Location added successfully."
          onSuccess={fetchLocations}
          selectedRowKeys={
            storeRole === process.env.REACT_APP_SROLE && selectedRowKeys
          }
          deleteOnClick={() => {
            handleDelete({
              selectedRowKeys,
              dispatch,
              deleteThunk: deleteLocationThunk,
              route: currentPath,
              actionName: "location",
              onSuccess: () => setSelectedRowKeys([]),
            });
          }}
          searchText={searchText}
          setSearchText={(value) => {
            setShowLoader(true);
            setSearchText(value);
            setTimeout(() => setShowLoader(false), 500);
          }}
        />

        <div
          className={`card p-5 rounded-lg paddingTop-0 ${
            storeTotalCount <= process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE
              ? "paddingBottom-0"
              : "minHeight-100vh"
          }`}
        >
          <div className="table-area">
            <div className="table-responsive">
              <div className="tab-content" id="alltable-size">
                <Form form={tableForm} component={false}>
                  <TableContent
                    bordered={false}
                    columns={mergedColumns}
                    rowClassName="editable-row"
                    rowSelection={
                      storeRole === process.env.REACT_APP_SROLE && rowSelection
                    }
                    storeList={filteredLocations}
                    components={{
                      body: {
                        cell: EditableCell,
                      },
                    }}
                    pagination={storePageInfo}
                    loading={showLoader}
                    totalCount={storeTotalCount}
                    handlePaginationChange={(page, pageSize) =>
                      deFaultHandlePaginationChange(
                        (type, pagination) =>
                          dispatch(
                            getLocationListThunk({
                              route: currentPath,
                              pagination,
                            })
                          ),
                        page,
                        pageSize,
                        storeTotalCount
                      )
                    }
                  />
                </Form>
              </div>
            </div>
          </div>
        </div>
        <EditForm
          title="Edit Location"
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          form={tableForm}
          fields={[
            {
              label: "Location Name",
              name: "location",
              rules: [
                { required: true, message: "Please input location" },
              ],
              type: "input",
            },
            {
              label: "Location Code",
              name: "locationCode",
              rules: [
                { required: true, message: "Please input location code!" },
              ],
              type: "input",
            },
            {
              label: "Location Currency Code",
              name: "locationCurrencyCode",
              rules: [
                { required: true, message: "Please input location currency code!" },
              ],
              type: "input",
            },
          ]}
          submitThunk={editLocationThunk}
          payloadBuilder={(values) => ({
            id: editingLocation.key,
            location: values.location,
            locationCode: values.locationCode,
            locationCurrencyCode: values.locationCurrencyCode,
            historySource: "backend",
            historyAction: "update",
            historyUserID: storeLIUId,
          })}
          successMessage="Location updated successfully."
          currentPath={currentPath}
          refreshListThunk={getLocationListThunk}
        />
      </div>
    </div>
  );
};
export default Locations;
